import { ITheme, IStyle, mergeStyleSets, getTheme, Icon } from '@fluentui/react';
import React from 'react';
import { ITimeBlockListing } from '../../../../entities-generated/models/time-block/time-block-listing';
import { noWrapStyle } from '../../../../framework/utils/no-wrap-style';
import { formatString } from '../../../../framework/utils/string';
import { toDate } from '../../../../framework/utils/date-time';

interface IInstallationDateListingProps {
  selected: boolean;
  timeBlock: ITimeBlockListing;
  onSelected: (time: ITimeBlockListing) => void;
}

const getStyleClasses = (theme: ITheme) => {
  const greyOutColor = theme.palette.neutralSecondaryAlt;
  const icon: IStyle = {
    fontSize: theme.fonts.large.fontSize,
    color: greyOutColor,
  };

  const iconDefault: IStyle = {
    ...icon,
    display: 'block',
  };

  const iconSelected: IStyle = {
    ...icon,
    display: 'none',
  };

  const heading: IStyle = {
    ...noWrapStyle as any,
    fontSize: theme.fonts.mediumPlus.fontSize,
    lineHeight: theme.fonts.mediumPlus.lineHeight,
    fontWeight: 500,
    color: greyOutColor,
    margin: 0,
    marginBottom: '0.125rem',
    padding: 0,
  };
  
  const subtext: IStyle = {
    ...noWrapStyle as any,
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    fontWeight: 400,
    color: greyOutColor,
    margin: 0,
    padding: 0,
  };

  const rootBase: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    padding: '0.5rem',
    borderRadius: 5,
    border: `2px solid ${greyOutColor}`,
    cursor: 'pointer',
    marginBottom: '0.5rem',
  };

  const root: IStyle = {
    ...rootBase,

    '&:hover': {
      borderColor: theme.palette.neutralPrimary,
    },

    '& .icon-default': iconDefault,
    '&:hover .icon-default': {
      ...iconDefault,
      display: 'none',
    },

    '& .icon-selected': iconSelected,
    '&:hover .icon-selected': {
      ...iconSelected,
      display: 'block',
      color: theme.palette.neutralPrimary,
    },

    '& h3': heading,
    '&:hover h3': {
      ...heading as any,
      color: theme.palette.neutralPrimary,
    },

    'p': subtext,
    '&:hover p': {
      ...subtext as any,
      color: theme.palette.neutralPrimary,
    },
  };

  const rootSelected: IStyle = {
    ...rootBase,
    borderColor: theme.palette.themePrimary,

    '& .icon-default': {
      display: 'none',
    },

    '& .icon-selected': {
      ...iconSelected,
      display: 'block',
      color: theme.palette.themePrimary,
    },

    '& h3': {
      ...heading as any,
      color: theme.palette.themePrimary,
    },

    'p': {
      ...subtext as any,
      color: theme.palette.themePrimary,
    },
  };

  const iconContainer: IStyle = {
    display: 'flex',
    alignItems: 'center',
  };

  const content: IStyle = {
    paddingLeft: '1rem',
    width: 1,
    flexGrow: 1,
  };

  return mergeStyleSets({
    root,
    rootSelected,
    iconContainer,
    content,
  });
}

export const InstallationDateListing: React.FunctionComponent<IInstallationDateListingProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { timeBlock } = props;
  const heading = timeBlock.date !== 0
    ? formatString('{d:d-wwwMMMd}', {d: toDate(timeBlock.date)}).formattedString
    : 'Contact me for scheduling';
  const subtext = timeBlock.date !== 0
    ? timeBlock.sequenceNumber < 13 ? 'Morning (8:00 - 11:30 AM arrival)' : 'Afternoon (12:00 - 4:30 PM arrival)'
    : 'We\'ll call/email to make arrangements ASAP';

  return (
    <div
      className={props.selected ? cs.rootSelected : cs.root}
      onClick={() => props.onSelected(props.timeBlock)}
    >
      <div className={cs.iconContainer}>
        <Icon className='icon-default' iconName='RadioBtnOff' />
        <Icon className='icon-selected' iconName='RadioBtnOn' />
      </div>
      <div className={cs.content}>
        <h3>{heading}</h3>
        <p>{subtext}</p>
      </div>
    </div>
  );
};
